import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { DashShell, Kpi, StatusPill } from "@/components/DashShell";
import { useLang } from "@/i18n";
import { api, fmtErr, inr, API } from "@/api";
import { DSA_SLABS } from "@/constants";
import { Copy } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsappButton";
import { useAuth } from "@/auth";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function DsaDashboard() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [idBusy, setIdBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const photoRef = useRef(null);

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", photoFile);
      await api.post("/dsa/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("msg_photo_uploaded"));
      setPhotoFile(null);
      setPhotoPreview(null);
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setUpBusy(false);
    }
  };

  const downloadId = async () => {
    setIdBusy(true);
    try {
      const r = await api.get("/dsa/id-card", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `matrix-dsa-id-${data?.dsa_code || "card"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        try {
          const txt = JSON.parse(await e.response.data.text());
          toast.error(txt.detail || t("dsad_photo_required"));
        } catch {
          toast.error(t("dsad_photo_required"));
        }
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setIdBusy(false);
    }
  };

  useEffect(() => {
    api.get("/dsa/overview").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/dsa/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(data?.dsa_code || "");
    toast.success(t("dsad_copied"));
  };

  const currentTotal = data?.slab?.total_pct ?? 0;

  const chartData = (() => {
    const rows = [...payouts].sort((a, b) => a.month.localeCompare(b.month));
    const curMonth = new Date().toISOString().slice(0, 7);
    const list = rows.map((p) => ({ month: p.month, business: p.business, total: p.earnings?.total ?? 0 }));
    const cur = list.find((r) => r.month === curMonth);
    if (data) {
      if (cur) {
        cur.business = data.business;
        cur.total = data.earnings.total;
      } else {
        list.push({ month: curMonth, business: data.business, total: data.earnings.total });
      }
    }
    return list;
  })();

  const GrowthChart = () => (
    <div className="bg-white border border-[#E5E2D9] p-7 mb-12" data-testid="dsa-growth-chart">
      <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("dsad_growth")}</h3>
      <p className="text-xs text-[#5C5C5C] mt-1 mb-6">{t("dsad_growth_sub")}</p>
      {chartData.length === 0 ? (
        <div className="text-center text-[#5C5C5C] text-sm py-10">{t("d_no_entries")}</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E2D9" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#5C5C5C" }} axisLine={{ stroke: "#E5E2D9" }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#5C5C5C" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} width={60} />
              <Tooltip
                formatter={(value, name) => [inr(value), name === "business" ? t("d_business") : t("slab_total")]}
                contentStyle={{ border: "1px solid #E5E2D9", borderRadius: 0, fontSize: 13 }}
              />
              <Area type="monotone" dataKey="business" stroke="#B5952F" strokeWidth={2.5} fill="url(#goldFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );

  return (
    <DashShell title={t("dsad_title")} subtitle={t("dsad_sub")} testid="dsa-dashboard">
      {data && (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#1A1A1A] text-white p-8 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6 noise-overlay"
            data-testid="dsa-code-card"
          >
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#D4AF37]">{t("dsad_your_code")}</div>
              <div className="font-display text-4xl font-bold mt-2 tracking-wide" data-testid="dsa-code-value">{data.dsa_code}</div>
              <p className="text-sm text-white/60 mt-2">{t("dsad_share_hint")}</p>
              <div className="mt-4 flex items-center gap-2 bg-[#232323] border border-[#3A3A3A] px-4 py-2.5 max-w-md" data-testid="dsa-referral-link">
                <span className="text-xs text-white/70 truncate tabular">{`${window.location.origin}/register?ref=${data.dsa_code}`}</span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={copy}
                className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-sm font-bold hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-200 w-fit"
                data-testid="dsa-copy-code-button"
              >
                <Copy size={16} /> {t("dsad_copy")}
              </button>
              <button
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/register?ref=${data.dsa_code}`); toast.success(t("msg_link_copied")); }}
                className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-sm font-bold hover:bg-white hover:text-[#1A1A1A] transition-colors duration-200 w-fit"
                data-testid="dsa-copy-link-button"
              >
                <Copy size={16} /> {t("dsad_copy_link")}
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${t("dsad_share_msg")} ${window.location.origin}/register?ref=${data.dsa_code}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-sm font-bold hover:bg-[#1da851] transition-colors duration-200 w-fit"
                data-testid="dsa-share-whatsapp-button"
              >
                <WhatsAppIcon size={16} /> {t("dsad_share_wa")}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 }}
            className="bg-white border border-[#E5E2D9] p-7 mb-12"
            data-testid="dsa-id-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 bg-[#F3F1EB] border border-[#E5E2D9] flex items-center justify-center overflow-hidden shrink-0" data-testid="dsa-photo-preview">
                  {photoPreview ? (
                    <img src={photoPreview} alt="DSA" className="w-full h-full object-cover" />
                  ) : user?.photo_path ? (
                    <img src={`${API}/files/${user.photo_path}`} alt="DSA" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-bold text-[#5C5C5C] uppercase tracking-wider text-center px-1">{t("d_photo")}</span>
                  )}
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("dsad_id_card")}</h3>
                  <p className="text-sm text-[#5C5C5C] mt-1 max-w-sm">{t("dsad_id_sub")}</p>
                </div>
              </div>
              <div className="flex flex-col gap-3 shrink-0">
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setPhotoFile(f);
                      setPhotoPreview(URL.createObjectURL(f));
                    }
                  }}
                  data-testid="dsa-photo-input"
                />
                <button
                  onClick={() => (photoFile ? uploadPhoto() : photoRef.current?.click())}
                  disabled={upBusy}
                  className="border-2 border-[#1A1A1A] text-[#1A1A1A] px-6 py-3 text-sm font-bold hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-200 disabled:opacity-50"
                  data-testid="dsa-photo-upload-button"
                >
                  {upBusy ? "..." : photoFile ? t("dsad_upload_photo") : t("d_proof_choose")}
                </button>
                <button
                  onClick={downloadId}
                  disabled={idBusy || !user?.photo_path}
                  className="bg-[#1A1A1A] text-[#D4AF37] px-6 py-3 text-sm font-bold hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-200 disabled:opacity-50"
                  data-testid="dsa-id-download-button"
                >
                  {idBusy ? "..." : t("dsad_download_id")}
                </button>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="dsa-kpi-strip">
            <Kpi label={t("dsad_team_business")} value={inr(data.business)} gold testid="kpi-dsa-business" />
            <Kpi label={t("dsad_current_slab")} value={`${currentTotal}%`} testid="kpi-dsa-slab" />
            <Kpi label={t("dsad_est_payout")} value={inr(data.earnings.total)} testid="kpi-dsa-earnings" />
            <Kpi label={t("dsad_team_members")} value={data.team.length} testid="kpi-dsa-team" />
          </div>

          <div className="grid lg:grid-cols-12 gap-6 mb-12">
            <div className="lg:col-span-5 bg-white border border-[#E5E2D9] p-7" data-testid="dsa-earnings-breakdown">
              <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-5">{t("dsad_breakdown")}</h3>
              {[
                { label: `${t("slab_payout")} (${data.slab.payout_pct}%)`, value: data.earnings.payout },
                { label: `${t("slab_renewal")} (${data.slab.renewal_pct}%)`, value: data.earnings.renewal },
                { label: `${t("slab_extra")} (${data.slab.extra_pct}%)`, value: data.earnings.extra },
              ].map((r) => (
                <div key={r.label} className="flex justify-between py-3 border-b border-[#E5E2D9] text-sm">
                  <span className="text-[#5C5C5C]">{r.label}</span>
                  <span className="tabular font-bold text-[#1A1A1A]">{inr(r.value)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-5">
                <span className="font-bold text-[#1A1A1A]">{t("slab_total")}</span>
                <span className="font-display text-2xl font-bold tabular text-[#B5952F]" data-testid="dsa-total-earnings">{inr(data.earnings.total)}</span>
              </div>
              <div className="mt-6">
                <div className="flex justify-between text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-2">
                  <span>{t("dsad_slab_progress")}</span><span>{currentTotal}% / 14%</span>
                </div>
                <div className="h-2 bg-[#F3F1EB]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(currentTotal / 14) * 100}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full bg-[#D4AF37]"
                    data-testid="dsa-slab-progress"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 bg-white border border-[#E5E2D9]" data-testid="dsa-team-table-card">
              <div className="px-7 py-5 border-b border-[#E5E2D9]">
                <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("dsad_my_team")}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C] border-b border-[#E5E2D9]">
                      <th className="px-7 py-4 font-bold">{t("d_name")}</th>
                      <th className="px-7 py-4 font-bold">{t("d_phone")}</th>
                      <th className="px-7 py-4 font-bold">{t("d_status")}</th>
                      <th className="px-7 py-4 font-bold text-right">{t("d_business")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.team.length === 0 && (
                      <tr><td colSpan={4} className="px-7 py-8 text-center text-[#5C5C5C]" data-testid="team-empty">{t("dsad_team_empty")}</td></tr>
                    )}
                    {data.team.map((m, i) => (
                      <tr key={i} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`team-row-${i}`}>
                        <td className="px-7 py-4">
                          <div className="font-semibold text-[#1A1A1A]">{m.name}</div>
                          <div className="text-xs text-[#5C5C5C]">{m.email}</div>
                        </td>
                        <td className="px-7 py-4 tabular text-[#5C5C5C]">{m.phone || "—"}</td>
                        <td className="px-7 py-4"><StatusPill status={m.status} /></td>
                        <td className="px-7 py-4 text-right tabular font-bold text-[#1A1A1A]">{inr(m.business)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <GrowthChart />

          <div className="bg-white border border-[#E5E2D9] mb-12" data-testid="dsa-payout-history">
            <div className="px-7 py-5 border-b border-[#E5E2D9]">
              <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("dsad_history")}</h3>
              <p className="text-xs text-[#5C5C5C] mt-1">{t("dsad_hist_sub")}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C] border-b border-[#E5E2D9]">
                    <th className="px-7 py-4 font-bold">{t("dsad_month")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("d_business")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("dsad_current_slab")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_payout")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_renewal")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_extra")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_total")}</th>
                    <th className="px-7 py-4 font-bold">{t("d_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.length === 0 && (
                    <tr><td colSpan={8} className="px-7 py-8 text-center text-[#5C5C5C]" data-testid="payouts-empty">{t("adm_no_payouts")}</td></tr>
                  )}
                  {payouts.map((p) => (
                    <tr key={p.id} className="border-b border-[#E5E2D9] last:border-0 hover:bg-[#FDFCF8]" data-testid={`payout-row-${p.id}`}>
                      <td className="px-7 py-4 font-semibold text-[#1A1A1A] tabular">{p.month}</td>
                      <td className="px-7 py-4 text-right tabular">{inr(p.business)}</td>
                      <td className="px-7 py-4 text-right tabular">{p.slab_pct}%</td>
                      <td className="px-7 py-4 text-right tabular">{inr(p.earnings?.payout)}</td>
                      <td className="px-7 py-4 text-right tabular">{inr(p.earnings?.renewal)}</td>
                      <td className="px-7 py-4 text-right tabular">{inr(p.earnings?.extra)}</td>
                      <td className="px-7 py-4 text-right tabular font-bold text-[#B5952F]">{inr(p.earnings?.total)}</td>
                      <td className="px-7 py-4"><StatusPill status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-[#E5E2D9]" data-testid="dsa-slab-reference">
            <div className="px-7 py-5 border-b border-[#E5E2D9]">
              <h3 className="font-display text-xl font-bold text-[#1A1A1A]">{t("dsad_slabs_title")}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#5C5C5C] border-b border-[#E5E2D9]">
                    <th className="px-7 py-4 font-bold">{t("slab_business")} (₹)</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_payout")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_renewal")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_extra")}</th>
                    <th className="px-7 py-4 font-bold text-right">{t("slab_total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {DSA_SLABS.map((s, i) => {
                    const total = s.payout + s.renewal + s.extra;
                    const active = total === currentTotal && data.business > 0;
                    return (
                      <tr key={i} className={`border-b border-[#E5E2D9] last:border-0 ${active ? "bg-[#F7F0D8]" : ""}`} data-testid={`slab-row-${i}`}>
                        <td className="px-7 py-4 tabular font-semibold text-[#1A1A1A]">
                          {s.above ? `₹${s.label}` : s.upto ? `${t("slab_upto")} ₹${s.label}` : `₹${s.label}`}
                          {active && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#B5952F]">● {t("dsad_you")}</span>}
                        </td>
                        <td className="px-7 py-4 text-right tabular">{s.payout}%</td>
                        <td className="px-7 py-4 text-right tabular">{s.renewal}%</td>
                        <td className="px-7 py-4 text-right tabular">{s.extra}%</td>
                        <td className="px-7 py-4 text-right tabular font-bold text-[#B5952F]">{total}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </DashShell>
  );
}
